import type { TFunction } from "i18next";
import { BuildResultToastBody } from "@/features/routes/components/BuildResultToastBody";
import {
  buildUnassignedBreakdown,
  parseUnassignedPayload,
  summarizeUnassignedByAddress,
  type UnassignedBreakdownPayload,
  type UnassignedNotificationOrder,
} from "@/features/routes/lib/unassignedBreakdown";
import {
  describeBuildWarnings,
  parseBuildWarnings,
  type BuildWarning,
} from "@/features/routes/lib/buildWarnings";
import { toastError, toastId, toastSuccess, toastWarning } from "@/lib/toast";
import {
  genericBuildFailureBodies,
  translateBuildFailure,
} from "@/features/notifications/lib/translateNotification";

/**
 * The ONE toast a route build produces (BUG-676).
 *
 * Two surfaces learn about a finished build: the launching tab's poller
 * (`useBuildRoutePoller`, full result) and the tenant-wide `routes_built`
 * notification over the WebSocket (`useNotificationsWebSocket`, a capped
 * breakdown). Each used to raise its own toast — an amber list from the poller
 * and a RED «Маршруты построены» from the socket — so one build showed twice,
 * once in the error style although 15 routes were created.
 *
 * Both now call {@link showBuildResultToast}:
 *   - the id is `<outcome>:build:result` for either source, so whichever lands
 *     second REPLACES the first in place instead of stacking a twin;
 *   - the socket additionally stays silent when this tab's poller already showed
 *     the same result (the poller's copy is the uncapped one);
 *   - the poller's «result unavailable» fallback stays silent when the socket
 *     already showed the real result.
 * The bell row is untouched — it is the persistent record, not a toast.
 */

export type BuildResultOutcome = "success" | "warning" | "error";
export type BuildResultSource = "poller" | "notification";

export interface BuildResult {
  routesCount: number;
  /** The dropped orders, or `null` when every order was placed. */
  unassigned: UnassignedBreakdownPayload | null;
  /**
   * BUG-691: coded warnings of a build that completed — e.g. routes planned on a
   * straight-line estimate (`PLANNING_MATRIX_ESTIMATED`). Absent = none.
   */
  warnings?: BuildWarning[];
  /**
   * BUG-694: the build's route date when it is ONE date, so a next-night window
   * reads «ночь на 23 окт.». Absent → «ночь после даты маршрута».
   */
  routeDate?: string | null;
}

/**
 * Nothing created → error; routes created but some orders dropped → warning;
 * every order placed → success. A build that created routes is never an error.
 */
export function buildResultOutcome(result: BuildResult): BuildResultOutcome {
  if (result.routesCount === 0) return "error";
  return (result.unassigned?.unassigned_total ?? 0) > 0 ? "warning" : "success";
}

/** How long a shown result suppresses its twin from the other source. */
const DEDUPE_WINDOW_MS = 60_000;

interface ShownResult {
  source: BuildResultSource;
  signature: string;
  at: number;
}

let lastShown: ShownResult | null = null;

const signatureOf = (result: BuildResult): string =>
  `${result.routesCount}:${result.unassigned?.unassigned_total ?? 0}`;

/** Test hook: forget the last shown result (and the last shown failure). */
export function resetBuildResultToastState(): void {
  lastShown = null;
  lastFailure = null;
}

/**
 * True when the `routes_built` NOTIFICATION showed a build result within the
 * dedupe window — the poller's «result unavailable» fallback then has nothing
 * to add. A result the poller itself showed earlier does not count: that was a
 * previous build.
 */
export function buildResultShownByNotificationRecently(
  now: number = Date.now(),
): boolean {
  return (
    (lastShown !== null &&
      lastShown.source === "notification" &&
      now - lastShown.at < DEDUPE_WINDOW_MS) ||
    (lastFailure !== null &&
      lastFailure.source === "notification" &&
      now - lastFailure.at < FAILURE_DEDUPE_WINDOW_MS)
  );
}

// ---------------------------------------------------------------------------
// The FAILURE path (BUG-679)
// ---------------------------------------------------------------------------
//
// A failed build is reported twice as well: the poller reads the task payload
// (`{status: "failed", error, code, params}`) and the socket delivers the
// ROUTES_BUILD_FAILED notification (`extra_data.code/params/reason`). Each used
// to raise its own red toast — live, the poller's raw «CP Solver fail» stacked
// on the notification's generic sentence. Now both render through ONE
// localizer (`translateBuildFailure`, the bell's own) and share ONE id, so
// whichever arrives second replaces the first in place; and a GENERIC rendering
// never replaces a more specific one already on screen.

/** Short: both sources report the same failure within a couple of poll ticks. */
const FAILURE_DEDUPE_WINDOW_MS = 15_000;

interface ShownFailure {
  source: BuildResultSource;
  /** 0 = a generic sentence (no code / INTERNAL_ERROR), 1 = a specific cause. */
  rank: number;
  at: number;
}

let lastFailure: ShownFailure | null = null;

/** What a failed build reports, in the notification's `extra_data` shape. */
export interface BuildFailure {
  code?: string | null;
  params?: Record<string, unknown> | null;
  /** The server's sentence — English; rendered only as a framed last resort. */
  reason?: string | null;
  [key: string]: unknown;
}

/**
 * Shows the ONE failure toast of a build, from either source. Persistent: the
 * dispatcher has to read why nothing was built before it goes.
 */
export function showBuildFailureToast(
  failure: BuildFailure | null | undefined,
  source: BuildResultSource,
): void {
  const now = Date.now();
  const { title, body } = translateBuildFailure(failure ?? null);
  const rank =
    body !== undefined && !genericBuildFailureBodies().includes(body) ? 1 : 0;
  if (
    lastFailure !== null &&
    now - lastFailure.at < FAILURE_DEDUPE_WINDOW_MS &&
    lastFailure.rank > rank
  ) {
    // The other source already showed the more specific cause.
    return;
  }
  lastFailure = { source, rank, at: now };
  toastError(title, body, {
    id: toastId("error", "build", "result"),
    persistent: true,
  });
}

/**
 * The ONE route date a build result covers, or `null` when it spans several
 * dates or names none (BUG-694) — read off `by_date` and the built routes.
 */
export function singleBuildDate(
  routes: readonly { date?: string | null }[],
  byDate: readonly { date?: string | null }[],
): string | null {
  const dates = new Set(
    [...byDate, ...routes]
      .map((entry) => entry.date?.trim() ?? "")
      .filter((date) => date !== ""),
  );
  return dates.size === 1 ? [...dates][0] : null;
}

/**
 * Builds the poller's payload from the full (uncapped) result list.
 */
export function payloadFromUnassignedOrders(
  orders: UnassignedNotificationOrder[],
): UnassignedBreakdownPayload | null {
  if (orders.length === 0) return null;
  return {
    unassigned_total: orders.length,
    unassigned_truncated: false,
    unassigned_reason_counts: orders.reduce<Record<string, number>>(
      (acc, order) => {
        acc[order.reason] = (acc[order.reason] ?? 0) + 1;
        return acc;
      },
      {},
    ),
    unassigned: orders,
  };
}

/** Build-outcome codes a `routes_built` notification carries (TASK-4743). */
const BUILD_OUTCOME_CODES = new Set([
  "ROUTES_BUILD_SUCCESS",
  "ROUTES_BUILD_PARTIAL",
  "ROUTES_BUILD_NONE",
]);

/**
 * Reads a `routes_built` notification's `extra_data` as a build result, or
 * `null` when it carries no outcome code / route count (a row written before
 * TASK-4743) — the caller then keeps the generic notification toast.
 */
export function parseBuildResultNotification(
  extraData: Record<string, unknown> | null | undefined,
): BuildResult | null {
  if (!extraData) return null;
  const code = extraData.code;
  if (typeof code !== "string" || !BUILD_OUTCOME_CODES.has(code)) return null;
  const params = extraData.params;
  if (typeof params !== "object" || params === null) return null;
  const routesCount = Number((params as Record<string, unknown>).routes_count);
  if (!Number.isFinite(routesCount)) return null;

  let unassigned = parseUnassignedPayload(extraData);
  if (unassigned === null) {
    // A breakdown-less row still states its count; keep the headline truthful.
    const count = Number((params as Record<string, unknown>).unassigned_count);
    if (Number.isFinite(count) && count > 0) {
      unassigned = {
        unassigned_total: count,
        unassigned_truncated: true,
        unassigned_reason_counts: {},
        unassigned: [],
      };
    }
  }
  return {
    routesCount,
    unassigned,
    warnings: parseBuildWarnings(extraData.warnings),
  };
}

/** The headline: «Создано 15 маршрутов, 27 заказов не распределено». */
function titleFor(
  result: BuildResult,
  outcome: BuildResultOutcome,
  t: TFunction,
): string {
  const unassignedCount = result.unassigned?.unassigned_total ?? 0;
  const created = t("toast.buildRoutesCreated", { count: result.routesCount });
  const dropped = t("toast.buildOrdersUnassigned", { count: unassignedCount });
  if (outcome === "success") return created;
  if (outcome === "error") return t("toast.buildNone", { unassigned: dropped });
  return t("toast.buildPartial", { routes: created, unassigned: dropped });
}

/**
 * Shows the build-result toast for `source`, or does nothing when the other
 * source already showed this very result (see the module comment).
 */
export function showBuildResultToast(
  result: BuildResult,
  source: BuildResultSource,
  t: TFunction,
): void {
  const now = Date.now();
  const signature = signatureOf(result);
  if (
    source === "notification" &&
    lastShown !== null &&
    lastShown.source === "poller" &&
    lastShown.signature === signature &&
    now - lastShown.at < DEDUPE_WINDOW_MS
  ) {
    return;
  }
  lastShown = { source, signature, at: now };

  const outcome = buildResultOutcome(result);
  const title = titleFor(result, outcome, t);
  // BUG-691: a build that placed everything but carries a warning (approximate
  // times) is NOT a plain green success — it is shown as a warning, so the
  // dispatcher reads why before it goes. The headline stays «Создано N маршрутов».
  const warnings = describeBuildWarnings(result.warnings, t);
  const flavour: BuildResultOutcome =
    outcome === "success" && warnings.length > 0 ? "warning" : outcome;
  const id = toastId(flavour, "build", "result");

  if (flavour === "success") {
    toastSuccess(title, undefined, { id });
    return;
  }

  const summaries = result.unassigned
    ? summarizeUnassignedByAddress(
        buildUnassignedBreakdown(result.unassigned, t, {
          routeDate: result.routeDate,
        }),
        t,
      )
    : [];
  const description =
    summaries.length > 0 || warnings.length > 0 ? (
      <BuildResultToastBody summaries={summaries} warnings={warnings} />
    ) : undefined;

  // Persistent: dropped orders still need a human decision, and the dispatcher
  // must be able to read which ones before the toast goes.
  const show = flavour === "error" ? toastError : toastWarning;
  show(title, description, { id, persistent: true });
}
